import * as React from "react";
import {
  Table,
  Link,
  SpaceBetween,
  Header,
  CopyToClipboard,
  Button,
} from "@amzn/awsui-components-console";
import { useJemGen } from "src/context/jem-gen/jemGenContext";
import { InitDomain } from "src/context/domain/domainContext";
import {
  FusePlan,
  ListRegistrationsCommand,
  Registration,
} from "@amzn/fuse-accounting-central-datastore-client";
import { AccountingRegistrationModal } from "./accounting-registration-modal";
import { LinkRegistrationModal } from "./link-registration-modal";
import { SPLIT_PANEL_STICKY_HEADER_OFFSET } from "./split-panel.helper";

interface AssociatedPlansProps {}

export function AssociatedPlans(props: AssociatedPlansProps) {
  const {
    selectedArdDetailed,
    ardRegionMap,
    getFacdClient,
    fetchArdData,
    registrationsLoadingState,
    setRegistrationsLoadingState,
  } = useJemGen();
  const [showRegistrationModal, setShowRegistrationModal] =
    React.useState(false);
  const [selectedRegistrationId, setSelectedRegistrationId] = React.useState<
    string | null
  >(null);
  const [registrationModalMode, setRegistrationModalMode] = React.useState<
    "view" | "create"
  >("view");

  // State for link registration modal
  const [showLinkModal, setShowLinkModal] = React.useState(false);
  const [selectedPlanForLinking, setSelectedPlanForLinking] =
    React.useState<FusePlan | null>(null);
  const [registrationIdToLink, setRegistrationIdToLink] = React.useState<
    string | null
  >(null);

  // State for available registrations from ListRegistrationsCommand
  const [availableRegistrations, setAvailableRegistrations] = React.useState<
    Registration[]
  >([]);

  const { authService } = React.useContext(InitDomain);
  const canEdit = authService.ldap.includes("bart-admin");

  const selectedArd = selectedArdDetailed?.ard;

  // Map region to appropriate domains
  const getDomainForRegion = (region: string) => {
    // Map regions to Fender domains (these are the same as the region values)
    const fenderDomainMap: { [key: string]: string } = {
      "devo-FE": "devo-FE",
      "prod-FE": "prod-FE",
      "prod-US": "prod-NA",
      "prod-EU": "prod-EU",
    };

    // Map regions to Bart domains
    const bartDomainMap: { [key: string]: string } = {
      "devo-FE": "beta",
      "prod-FE": "prod-jp",
      "prod-US": "prod-us",
      "prod-EU": "prod-eu",
    };

    // Map regions to ORCA Studio domains
    const orcaDomainMap: { [key: string]: string } = {
      "devo-FE": "beta.us-west-2",
      "prod-FE": "us-west-2",
      "prod-US": "us-east-1",
      "prod-EU": "eu-south-2",
    };

    return {
      fender: fenderDomainMap[region] || "devo-FE",
      bart: bartDomainMap[region] || "beta",
      orca: orcaDomainMap[region] || "beta.us-west-2",
    };
  };

  // Get region for the selected ARD
  const getRegionForArd = (ardId: string): string => {
    return ardRegionMap?.get(ardId) || "devo-FE";
  };

  // Fetch registrations when selectedArd changes
  React.useEffect(() => {
    const fetchRegistrations = async () => {
      if (!selectedArd?.id) {
        setAvailableRegistrations([]);
        setRegistrationsLoadingState("loaded");
        return;
      }

      const domain = ardRegionMap.get(selectedArd.id);
      if (domain === undefined) {
        console.error(`Unknown region for ARD ${selectedArd.id}`);
        setAvailableRegistrations([]);
        setRegistrationsLoadingState("loaded");
        return;
      }

      setRegistrationsLoadingState("loading");

      try {
        const facdClient = await getFacdClient(domain);
        if (!facdClient) {
          console.error("No FACD client found for region:", domain);
          setAvailableRegistrations([]);
          return;
        }

        const listRegistrationsCommand = new ListRegistrationsCommand({
          ardId: selectedArd.id,
        });

        console.log(
          "Starting FACD listRegistrations operation:",
          listRegistrationsCommand,
        );

        const response = await facdClient.send(listRegistrationsCommand);

        console.log("ListRegistrations response:", response);

        if (response && response.registrations) {
          setAvailableRegistrations(response.registrations);
        } else {
          setAvailableRegistrations([]);
        }
      } catch (error) {
        console.error("An error occurred in listRegistrations:", error);
        setAvailableRegistrations([]);
      } finally {
        setRegistrationsLoadingState("loaded");
      }
    };

    fetchRegistrations();
  }, [
    selectedArd?.id,
    ardRegionMap,
    getFacdClient,
    setRegistrationsLoadingState,
  ]);

  // Find a registration ID from other plans in the same ARD
  const getRegistrationIdFromOtherPlans = React.useCallback(():
    | string
    | null => {
    const associatedPlans = selectedArd?.associatedPlans || [];
    for (const plan of associatedPlans) {
      if (plan.accountingRegistrationId) {
        return plan.accountingRegistrationId;
      }
    }
    return null;
  }, [selectedArd?.associatedPlans]);

  // Get available registration ID to link (from other plans or from ListRegistrations)
  const getAvailableRegistrationId = React.useCallback((): string | null => {
    // First check if another plan in the same ARD has a registration
    const registrationFromOtherPlans = getRegistrationIdFromOtherPlans();
    if (registrationFromOtherPlans) {
      return registrationFromOtherPlans;
    }

    // Then check if there are registrations from ListRegistrationsCommand
    if (availableRegistrations.length > 0) {
      // Use the first registration's accountingRegistrationId
      return availableRegistrations[0].accountingRegistrationId || null;
    }

    return null;
  }, [getRegistrationIdFromOtherPlans, availableRegistrations]);

  // Generate external links for a plan
  const generateExternalLinks = (plan: FusePlan) => {
    if (!selectedArd?.id) return null;

    const region = getRegionForArd(selectedArd.id);
    const domains = getDomainForRegion(region);

    const fenderUrl = `https://fender.corp.amazon.com/domains/${domains.fender}/plans/${plan.id}`;
    const bartUrl = `https://${domains.bart}.bart-portal.fuse.amazon.dev/getplan/${plan.id}`;
    const orcaUrl = `https://${domains.orca}.studio.orca.amazon.dev/#/clients/fuse-billing/executions/actions/query?filters=Tags+%253D+plan%253A${plan.id}`;

    return (
      <SpaceBetween direction="horizontal" size="s">
        <Link external href={fenderUrl}>
          Fender
        </Link>
        <Link external href={bartUrl}>
          Bart
        </Link>
        <Link external href={orcaUrl}>
          WF Samples
        </Link>
      </SpaceBetween>
    );
  };

  const handleRegistrationClick = React.useCallback(
    (registrationId: string) => {
      setSelectedRegistrationId(registrationId);
      setRegistrationModalMode("view");
      setShowRegistrationModal(true);
    },
    [],
  );

  const handleCreateRegistrationClick = React.useCallback(() => {
    setSelectedRegistrationId(null);
    setRegistrationModalMode("create");
    setShowRegistrationModal(true);
  }, []);

  const handleModalDismiss = React.useCallback(() => {
    setShowRegistrationModal(false);
    setSelectedRegistrationId(null);
    setRegistrationModalMode("view");
  }, []);

  const handleCreateSuccess = React.useCallback(() => {
    console.log("Registration created successfully");
    if (selectedArd?.id) {
      fetchArdData(selectedArd.id);
    }
  }, [selectedArd?.id, fetchArdData]);

  const handleLinkRegistrationClick = (
    plan: FusePlan,
    registrationId: string,
  ) => {
    setSelectedPlanForLinking(plan);
    setRegistrationIdToLink(registrationId);
    setShowLinkModal(true);
  };

  const handleLinkModalDismiss = () => {
    setShowLinkModal(false);
    setSelectedPlanForLinking(null);
    setRegistrationIdToLink(null);
  };

  const handleLinkSuccess = () => {
    console.log("Registration linked successfully");
    if (selectedArd?.id) {
      fetchArdData(selectedArd?.id);
    }
  };

  // Render the accounting registration ID cell
  const renderAccountingRegistrationCell = (plan: FusePlan) => {
    // If plan already has a registration ID, show it as a link
    if (plan.accountingRegistrationId) {
      return (
        <Button
          variant="inline-link"
          onClick={() =>
            handleRegistrationClick(plan.accountingRegistrationId!)
          }
        >
          {plan.accountingRegistrationId}
        </Button>
      );
    }

    // Plan doesn't have a registration ID - check if we can link one
    const availableRegId = getAvailableRegistrationId();

    if (availableRegId) {
      return (
        <Button
          variant="inline-link"
          onClick={() => handleLinkRegistrationClick(plan, availableRegId)}
        >
          Link Registration
        </Button>
      );
    }

    // No registration available to link - show create button (only for admins)
    if (!canEdit) {
      return "N/A";
    }

    return (
      <Button variant="inline-link" onClick={handleCreateRegistrationClick}>
        Create Registration
      </Button>
    );
  };

  const columnDefinitions = [
    {
      id: "id",
      header: "Plan ID",
      width: 350,
      cell: (plan: FusePlan) => {
        if (!plan.id) {
          return "N/A";
        }
        return (
          <CopyToClipboard
            copyButtonAriaLabel="Copy Plan ID"
            copyErrorText="Plan ID failed to copy"
            copySuccessText="Plan ID copied"
            textToCopy={plan.id}
            variant="inline"
          />
        );
      },
    },
    {
      id: "accountingRegistrationId",
      header: "Accounting Registration ID",
      minWidth: 320,
      cell: (plan: FusePlan) => renderAccountingRegistrationCell(plan),
    },
    {
      id: "fusenEnabled",
      header: "Fusen Enabled",
      width: 100,
      cell: (plan: FusePlan) => (plan.fusenEnabled ? "True" : "False"),
    },
    {
      id: "migrationTargetStage",
      header: "Migration Target Stage",
      width: 120,
      cell: (plan: FusePlan) => plan.migrationTargetStage || "N/A",
    },
    {
      id: "glLineDescriptionPartnerName",
      header: "GL Line Description Partner Name",
      width: 350,
      cell: (plan: FusePlan) => plan.glLineDescriptionPartnerName || "N/A",
    },
    {
      id: "fusenEnableDate",
      header: "Fusen Enable Date",
      width: 180,
      cell: (plan: FusePlan) => plan.fusenEnableDate || "N/A",
    },
    {
      id: "externalLinks",
      header: "External Links",
      minWidth: 300,
      cell: (plan: FusePlan) => generateExternalLinks(plan),
    },
  ];

  const associatedPlans = selectedArd?.associatedPlans || [];

  if (!selectedArd) {
    return <>No ARD selected</>;
  }

  return (
    <>
      <Table
        columnDefinitions={columnDefinitions}
        items={associatedPlans}
        loadingText="Loading associated plans"
        loading={registrationsLoadingState === "loading"}
        sortingDisabled={true}
        stickyHeader
        stickyHeaderVerticalOffset={SPLIT_PANEL_STICKY_HEADER_OFFSET}
        stickyColumns={{ first: 1, last: 1 }}
        empty={
          <SpaceBetween size="m">
            <div>No associated plans found</div>
          </SpaceBetween>
        }
        header={
          <Header variant="h3" counter={`(${associatedPlans.length})`}>
            Associated Plans
          </Header>
        }
      />

      <AccountingRegistrationModal
        visible={showRegistrationModal}
        accountingRegistrationId={selectedRegistrationId}
        onDismiss={handleModalDismiss}
        mode={registrationModalMode}
        ardForCreate={
          registrationModalMode === "create" ? selectedArd : undefined
        }
        allPlans={
          registrationModalMode === "create" ? associatedPlans : undefined
        }
        onCreateSuccess={handleCreateSuccess}
      />

      <LinkRegistrationModal
        visible={showLinkModal}
        plan={selectedPlanForLinking}
        registrationId={registrationIdToLink}
        onDismiss={handleLinkModalDismiss}
        onSuccess={handleLinkSuccess}
      />
    </>
  );
}
